/* eslint-disable react/forbid-prop-types */
/* eslint-disable no-param-reassign */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import * as d3 from 'd3';
import * as fc from 'd3fc';
import { scaleLinear } from 'd3-scale';
import { select } from 'd3-selection';
import { axisBottom, axisLeft } from 'd3-axis';
import colors from '../../../../../../../../apps/baykis-pl/pattern-lab/_data/generated_colors.json';
import './boxplot.scss';
import LegendBoxplot from './legendBoxplot.jsx';

const calculationMinMax = (min, max, maxValue, minValue) => {
  maxValue = maxValue <= max ? max : maxValue;
  minValue = minValue >= min ? min : minValue;

  return {
    maxValue,
    minValue,
  };
};

const isSiteInDarkMod = () => {
  const el = document.querySelector('html[data-theme]');

  if (el) {
    const { theme } = el.dataset;

    if (theme) {
      return theme === 'dark';
    }
  }
  return false;
};

const getTimeWindowedGraphData = (dataset, windowBegin, windowEnd) => {
  if (!dataset[0]?.length) {
    return;
  }
  const firstIndex = dataset[0].findIndex(
    (element) => element.timestamp === windowBegin
  );
  const windowSize = windowEnd - windowBegin;

  const lastIndex =
    firstIndex + windowSize > dataset[0].length - 1
      ? dataset[0].length
      : firstIndex + windowSize;
  const windowedData = [
    dataset[0].slice(firstIndex, lastIndex),
    dataset[1].slice(firstIndex, lastIndex),
    dataset[2].slice(firstIndex, lastIndex),
  ];
  return windowedData;
};

class Boxplot extends Component {
  constructor(props) {
    super(props);

    const { graphData, epoch } = this.props;

    this.createChart = this.createChart.bind(this);
    this.labelAxisYRef = React.createRef();
    this.placeholderChart = React.createRef();
    this.placeholderWrapper = React.createRef();
    this.graphData = [...graphData];
    this.epoch = epoch;
    this.timeWindowBegin = 2020;
    this.timeWindowEnd = 2100;
    this.windowedData = [];
    this.numberTicks = 5;

    this.themeSwitchHandler = this.themeSwitchHandler.bind(this);
    this.themeSwitcher = this.getThemeSwitcher();
    this.state = {
      chartSize: {
        width: 0,
        height: 0,
      },
      minValue: 0,
      maxValue: 0,
      minYAxis: 0,
      maxYAxis: 0,
      diffMaxMin: 0,
      siteInDarkMod: isSiteInDarkMod(),
    };

    this.winResize = this.winResize.bind(this);
    this.winResizeTimer = null;

    this.svg = d3.select('.svg');
  }

  static getRcpItemsByRcpFilter(rcpSelectedItems) {
    let rcp26Graph = null;
    let rcp45Graph = null;
    let rcp85Graph = null;

    if (rcpSelectedItems.length > 0) {
      rcp26Graph = rcpSelectedItems.find(
        (element) => element === 'rcp26_graph'
      );
      rcp45Graph = rcpSelectedItems.find(
        (element) => element === 'rcp45_graph'
      );
      rcp85Graph = rcpSelectedItems.find(
        (element) => element === 'rcp85_graph'
      );
    }

    return {
      rcp26Graph,
      rcp45Graph,
      rcp85Graph,
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.winResize);

    const chartSize = this.getChartSize();
    this.windowedData = getTimeWindowedGraphData(
      this.graphData,
      this.timeWindowBegin,
      this.timeWindowEnd
    );

    if (this.graphData.length > 0) {
      this.themeSwitcher.addEventListener();

      const { rcp } = this.props;
      const values = this.getMinMaxValues();

      this.setState({
        chartSize: {
          width: chartSize.width,
          height: chartSize.height,
        },
        minValue: values.minValue,
        maxValue: values.maxValue,
        minYAxis: values.minYAxis,
        maxYAxis: values.maxYAxis,
        diffMaxMin: values.diffMaxMin,
      });

      this.createChart(
        this.windowedData,
        this.epoch,
        Boxplot.getRcpItemsByRcpFilter(rcp.selectedItems)
      );
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { graphData, epoch, rcp, chartTitle } = this.props;

    if (
      graphData.length > 0 &&
      (chartTitle !== prevProps.chartTitle ||
        (prevState.maxYAxis === 0 && prevState.maxValue === 0) ||
        rcp.selectedItems.join('') !== prevProps.rcp.selectedItems.join(''))
    ) {
      this.createChart(
        graphData,
        epoch,
        Boxplot.getRcpItemsByRcpFilter(rcp.selectedItems)
      );
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.winResize);

    this.themeSwitcher.removeEventListener();
  }

  getThemeSwitcher() {
    const items = document.querySelectorAll('.theme-switcher');

    return {
      addEventListener: () => {
        items.forEach((item) => {
          item.addEventListener('click', this.themeSwitchHandler);
        });
      },
      removeEventListener: () => {
        items.forEach((item) => {
          item.removeEventListener('click', this.themeSwitchHandler);
        });
      },
    };
  }

  getChartSize() {
    const placeholderWrapper = this.placeholderWrapper.current;
    const labelAxisYRef = this.labelAxisYRef.current;

    const { pdfView } = this.props;

    let marginTop = 0;
    let marginRight = 0;
    let marginBottom = 0;
    let marginLeft = 0;

    if (placeholderWrapper) {
      const svg = placeholderWrapper.querySelector('svg');

      if (svg) {
        const style = window.getComputedStyle(svg, null);

        marginTop = parseInt(style.marginTop, 10);
        marginRight = parseInt(style.marginRight, 10);
        marginBottom = parseInt(style.marginBottom, 10);
        marginLeft = parseInt(style.marginLeft, 10);
      }
    }
    let chartWidth = placeholderWrapper
      ? placeholderWrapper.offsetWidth - (marginLeft + marginRight)
      : null;
    chartWidth -= labelAxisYRef ? labelAxisYRef.clientWidth + 20 : 0;

    if (pdfView) {
      const vw = 750;
      chartWidth = vw - (marginLeft + marginRight - 100);
    }

    return {
      width: chartWidth,
      height: chartWidth
        ? Math.round(chartWidth * 0.5) - (marginTop + marginBottom)
        : null,
    };
  }

  getMinMaxValues() {
    let { minValue, maxValue } = this.state;

    this.windowedData.forEach((element) => {
      element.forEach((el) => {
        const res = calculationMinMax(el.min, el.max, maxValue, minValue);

        maxValue = res.maxValue;
        minValue = res.minValue;
      });
    });
    const diffMaxMin = 0.2 * (maxValue - minValue);

    return {
      minYAxis: minValue,
      maxYAxis: maxValue,
      minValue,
      maxValue,
      diffMaxMin,
    };
  }

  getScale() {
    const { chartSize, minYAxis, maxYAxis } = this.state;
    const graphDomain = [2020, 2080];

    const xScale = scaleLinear()
      .domain(graphDomain)
      .range([0, chartSize.width]);

    const yScale = scaleLinear()
      .domain([minYAxis, maxYAxis])
      .nice(5)
      .range([chartSize.height, 0]);

    return {
      xScale,
      yScale,
    };
  }

  getBoxDataByTimeWindow(rcpValues, windowBegin) {
    const { yScale } = this.getScale();
    const firstIndex = rcpValues.findIndex(
      (element) => element.timestamp === windowBegin
    );
    const lastIndex = firstIndex + 29;

    const middleIndex = firstIndex + Math.floor((lastIndex - firstIndex) / 2);

    const minValue = rcpValues[middleIndex].min;
    const maxValue = rcpValues[middleIndex].max;
    const { median } = rcpValues[middleIndex];

    const height = Math.abs(yScale(maxValue) - yScale(minValue));

    return {
      maxValue: yScale(maxValue),
      height,
      medianValue: yScale(median),
    };
  }

  themeSwitchHandler() {
    const { rcp } = this.props;
    this.setState({ siteInDarkMod: isSiteInDarkMod() });

    this.createChart(
      this.windowedData,
      this.epoch,
      Boxplot.getRcpItemsByRcpFilter(rcp.selectedItems)
    );
  }

  addBoxes(data, timeZoneBegin, offset, boxColor, lineColor, visible) {
    const { xScale } = this.getScale();
    const siteInDarkMod = isSiteInDarkMod();

    let width = 30;

    const ticks = xScale.ticks(this.numberTicks);
    if (ticks.length > 1) {
      const tickDistance = Math.abs(
        xScale(ticks[ticks.length - 1]) - xScale(ticks[ticks.length - 2])
      );

      width = Math.floor(tickDistance / 5);
    }
    const boxPadding = Math.floor(width / 5);
    const boxMargin = Math.floor(width - boxPadding * 3);

    const svgWrapper = select(this.placeholderChart.current);
    const { maxValue, height, medianValue } = this.getBoxDataByTimeWindow(
      data,
      timeZoneBegin
    );
    const xPos =
      xScale(timeZoneBegin) + boxMargin + (width + boxPadding) * offset;

    // append white box for homogenous opacity
    svgWrapper
      .append('rect')
      .attr('width', width)
      .attr('height', () => {
        return height;
      })
      .attr('x', xPos)
      .attr('y', maxValue)
      .attr('fill', 'hsl(0, 0%, 100%, 0.7)')
      .style('opacity', 1.0)
      .attr('stroke-width', 0)
      .style('display', visible && !siteInDarkMod ? 'block' : 'none');

    svgWrapper
      .append('rect')
      .attr('width', width)
      .attr('height', () => {
        return height;
      })
      .attr('x', xPos)
      .attr('y', maxValue)
      .attr('fill', boxColor)
      .style('opacity', 1.0)
      .attr('stroke-width', 0)
      .style('display', visible ? 'block' : 'none');

    svgWrapper
      .append('line')
      .attr('x1', xPos)
      .attr('x2', xPos + width)
      .attr('y1', medianValue)
      .attr('y2', medianValue)
      .attr('stroke-width', 2)
      .attr('stroke', lineColor)
      .style('display', visible ? 'block' : 'none');
  }

  createChart(graphData, epoch, rcpItems) {
    const svgWrapper = select(this.placeholderChart.current);
    const { chartSize } = this.state;
    const { colorsets } = this.props;

    const siteInDarkMod = isSiteInDarkMod();
    if (colorsets !== undefined) {
      const { yScale, xScale } = this.getScale();

      xScale.range([0, chartSize.width]);
      yScale.range([chartSize.height, 0]);

      const formatXAxis = (d) => {
        return `${d + 1} - ${d + 30}`;
      };

      const xAxis = fc
        .axisBottom(xScale)
        .tickFormat(formatXAxis)
        .tickCenterLabel(true)
        .tickSize(0)
        .ticks(this.numberTicks);

      // grid
      const xAxisGrid = axisBottom(xScale)
        .tickSize(-chartSize.height)
        .tickFormat('')
        .ticks(this.numberTicks);
      const yAxisGrid = axisLeft(yScale)
        .tickSize(-chartSize.width)
        .tickFormat((domainValue) => `${domainValue}`.replace(/\./g, ','))
        .ticks(this.numberTicks);

      svgWrapper.selectAll('g').remove();
      svgWrapper.selectAll('path').remove();
      svgWrapper.selectAll('line').remove();
      svgWrapper.selectAll('text').remove();
      svgWrapper.selectAll('circle').remove();
      svgWrapper.selectAll('rect').remove();

      // x-Achse
      svgWrapper
        .append('g')
        .attr('class', 'x axis')
        .attr('stroke-width', '2px')
        .attr('transform', `translate(0, ${chartSize.height})`)
        .call(xAxis)
        .selectAll('text')
        .attr('y', 15)
        .attr('class', 'xLabel');

      svgWrapper
        .append('g')
        .attr('transform', `translate(0,${chartSize.height})`)
        .call(xAxisGrid);

      // y-Achse
      const yAxis = axisLeft()
        .tickSizeOuter(0)
        .tickFormat('')
        .scale(yScale)
        .tickSize(0);
      svgWrapper
        .append('g')
        .attr('class', 'y axis')
        .call(yAxis)
        .attr('y', -50)
        .attr('stroke-width', '2px');

      // y grid
      svgWrapper.append('g').call(yAxisGrid);

      const arrayRCP26 = graphData[0];
      const arrayRCP45 = graphData[1];
      const arrayRCP85 = graphData[2];

      let currentZoneBegin = 2021;
      let currentZoneEnd = 2050;

      while (currentZoneEnd <= 2100) {
        const rcp26Color = colors.themeBase.rcp26AreaWithAlphaParameter.replace(
          'alphaParameter',
          siteInDarkMod ? '.75' : '.7'
        );
        const rcp26Line = siteInDarkMod
          ? colors.themeBase.rcp26LineDarkMode
          : colors.themeBase.rcp26Line;

        const rcp45Color = colors.themeBase.rcp45AreaWithAlphaParameter.replace(
          'alphaParameter',
          siteInDarkMod ? '.7' : '.25'
        );

        const rcp45Line = siteInDarkMod
          ? colors.themeBase.rcp45LineDarkMode
          : colors.themeBase.rcp45Line;

        const rcp85Color = colors.themeBase.rcp85AreaWithAlphaParameter.replace(
          'alphaParameter',
          siteInDarkMod ? '.6' : '.7'
        );
        const { rcp85Line } = colors.themeBase;
        const { rcp26Graph, rcp45Graph, rcp85Graph } = rcpItems;

        this.addBoxes(
          arrayRCP26,
          currentZoneBegin,
          0,
          rcp26Color,
          rcp26Line,
          rcp26Graph
        );
        this.addBoxes(
          arrayRCP45,
          currentZoneBegin,
          1,
          rcp45Color,
          rcp45Line,
          rcp45Graph
        );
        this.addBoxes(
          arrayRCP85,
          currentZoneBegin,
          2,
          rcp85Color,
          rcp85Line,
          rcp85Graph
        );

        currentZoneEnd += 10;
        currentZoneBegin += 10;
      }
    }
  }

  winResize() {
    clearTimeout(this.winResizeTimer);

    this.winResizeTimer = setTimeout(() => {
      const { rcp } = this.props;
      const chartSize = this.getChartSize();

      this.setState({
        chartSize: {
          width: chartSize.width,
          height: chartSize.height,
        },
      });
      this.createChart(
        this.windowedData,
        this.epoch,
        Boxplot.getRcpItemsByRcpFilter(rcp.selectedItems)
      );
    }, 150);
  }

  render() {
    const { siteInDarkMod } = this.state;
    const { chartSize } = this.state;
    const { rcp, chartTitle, chartSubtitle, epoch, unit, colorsets } =
      this.props;
    const rcpItems = Boxplot.getRcpItemsByRcpFilter(rcp.selectedItems);
    const legendItems = [
      {
        classes: `${rcpItems.rcp26Graph ? '' : ' hide'}`,
        text: 'RCP 2.6',
        medianColor:
          colors.themeBase[`rcp26Line${siteInDarkMod ? 'DarkMode' : ''}`] ||
          colors.themeBase.rcp26Line,
        bandWidthColor: colors.themeBase.rcp26AreaWithAlphaParameter.replace(
          'alphaParameter',
          siteInDarkMod ? '.75' : '.7'
        ),
      },
      {
        classes: `${rcpItems.rcp45Graph ? '' : ' hide'}`,
        text: 'RCP 4.5',
        medianColor:
          colors.themeBase[`rcp45Line${siteInDarkMod ? 'DarkMode' : ''}`] ||
          colors.themeBase.rcp45Line,
        bandWidthColor: colors.themeBase.rcp45AreaWithAlphaParameter.replace(
          'alphaParameter',
          siteInDarkMod ? '.7' : '.25'
        ),
      },
      {
        classes: `${rcpItems.rcp85Graph ? '' : ' hide'}`,
        text: 'RCP 8.5',
        medianColor:
          colors.themeBase[`rcp85Line${siteInDarkMod ? 'DarkMode' : ''}`] ||
          colors.themeBase.rcp85Line,
        bandWidthColor: colors.themeBase.rcp85AreaWithAlphaParameter.replace(
          'alphaParameter',
          siteInDarkMod ? '.6' : '.7'
        ),
      },
    ];

    const changeUnit =
      colorsets[0] !== undefined ? colorsets[0].changeUnit : '';
    const labelY =
      epoch === 'past'
        ? `${chartTitle} in ${unit}`
        : `Änderung zu 1971 - 2000 in ${changeUnit}`;

    return (
      <div
        ref={this.placeholderWrapper}
        className={`boxplot chart-wrapper${rcp.viewIsActive ? ' rcp' : ''}`}
      >
        <h3 className="chart-title">{chartTitle}</h3>
        <h5 className="chart-subtitle">{chartSubtitle}</h5>
        <div className="svg-wrapper">
          <div
            ref={this.labelAxisYRef}
            className="labelingAxisY"
            style={{
              height: chartSize.height > 0 ? chartSize.height + 10 : '',
            }}
          >
            {labelY}
          </div>
          <svg
            className="svg"
            ref={this.placeholderChart}
            width={chartSize.width}
            height={chartSize.height}
          />
          <div className="labelingAxisX">Zeitraum</div>
        </div>
        {rcp.viewIsActive ? (
          <LegendBoxplot items={legendItems} />
        ) : (
          <LegendBoxplot />
        )}
        <p className="copyright">
          ©&nbsp;Bayerisches Klimainformationssystem LfU&nbsp;
          {new Date().getFullYear()}
        </p>
      </div>
    );
  }
}

Boxplot.propTypes = {
  graphData: PropTypes.array,
  rcp: PropTypes.shape({
    viewIsActive: PropTypes.bool.isRequired,
    selectedItems: PropTypes.array.isRequired,
  }).isRequired,
  chartTitle: PropTypes.string,
  chartSubtitle: PropTypes.string,
  epoch: PropTypes.string,
  unit: PropTypes.string,
  colorsets: PropTypes.array,
  pdfView: PropTypes.bool,
};

Boxplot.defaultProps = {
  graphData: [],
  chartTitle: '',
  chartSubtitle: '',
  epoch: '',
  unit: '',
  colorsets: [],
  pdfView: false,
};

export default Boxplot;
